import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    Card,
    DatePicker,
    Upload,
    message,
    Select,
    Space,
    Table,
    Statistic,
    Row,
    Col,
    Typography,
} from "antd";
import type {TablePaginationConfig, SorterResult} from "antd/es/table/interface";
import type {UploadFile, UploadProps, TableColumnsType} from "antd";
import dayjs, {Dayjs} from "dayjs";
import api from "../../api";

const {RangePicker} = DatePicker;
const {Title, Text} = Typography;

type AggTotals = {
    days_played: number | null;
    total_theo: number | null;
    total_win: number | null;
    slot_casino_win: number | null;
    tabl_casino_win: number | null;
    slot_theo_win: number | null;
    tabl_theo_win: number | null;
};

type AggMonthRow = AggTotals & { month: string };

type RawRow = {
    id?: number;
    month: string;
    mmid: string;
    customer_name?: string | null;
    host_name: string;
    agent?: string | null;
    club_tier: string;
    passport_country: string;
    days_played: number;
    total_theo: number;
    total_win: number;
    slot_casino_win: number;
    tabl_casino_win: number;
    slot_theo_win: number;
    tabl_theo_win: number;
};

type Option = { label: string; value: string };

function fmtInt(n: any) {
    const v = Number(n ?? 0);
    if (!Number.isFinite(v)) return "-";
    return Math.round(v).toLocaleString();
}

function monthLabel(iso: string) {
    return dayjs(iso).format("YYYY-MM");
}

function rangeSubtitle([start, end]: [Dayjs, Dayjs]) {
    return `Player performance between ${start.format("YYYY-MM")} and ${end.format("YYYY-MM")}`;
}

function PerformancePage() {
    // ---------------------------
    // Upload
    // ---------------------------
    const [uploadMonth, setUploadMonth] = useState<Dayjs>(dayjs().startOf("month"));
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    const uploadProps: UploadProps = {
        accept: ".csv",
        maxCount: 1,
        fileList,
        beforeUpload: () => false,
        onChange: ({fileList: next}) => setFileList(next.slice(-1)),
    };

    // ---------------------------
    // Filters + Results
    // ---------------------------
    const [range, setRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(0, "month").startOf("month"),
        dayjs().startOf("month"),
    ]);

    const [hostName, setHostName] = useState<string | undefined>(undefined);
    const [clubTier, setClubTier] = useState<string | undefined>(undefined);
    const [passportCountry, setPassportCountry] = useState<string | undefined>(undefined);
    const [customerNationality, setCustomerNationality] = useState<string | undefined>(undefined);
    const [canViewWinMetrics, setCanViewWinMetrics] = useState<boolean>(false);
    const [agent, setAgent] = useState<string | undefined>(undefined);
    const [agentOptions, setAgentOptions] = useState<Option[]>([]);

    const [loading, setLoading] = useState(false);
    const [totals, setTotals] = useState<AggTotals | null>(null);
    const [byMonth, setByMonth] = useState<AggMonthRow[]>([]);
    const [rawRows, setRawRows] = useState<RawRow[]>([]);
    const [rawTotal, setRawTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(50);

    const [optionsLoading, setOptionsLoading] = useState(false);
    const [hostOptions, setHostOptions] = useState<Option[]>([]);
    const [tierOptions, setTierOptions] = useState<Option[]>([]);
    const [passportOptions, setPassportOptions] = useState<Option[]>([]);
    const [custNatOptions, setCustNatOptions] = useState<Option[]>([]);
    const [ordering, setOrdering] = useState<string | undefined>(undefined);


    const queryParams = useMemo(() => {
        const [start, end] = range;
        const params: Record<string, string> = {
            start: start.startOf("month").format("YYYY-MM-DD"),
            end: end.startOf("month").format("YYYY-MM-DD"),
        };
        if (hostName) params.host_name = hostName;
        if (clubTier) params.club_tier = clubTier;
        if (passportCountry) params.passport_country = passportCountry;
        if (customerNationality) params.customer_nationality = customerNationality;
        if (agent) params.agent = agent;
        return params;
    }, [range, hostName, clubTier, passportCountry, customerNationality, agent]);

    const loadOptions = async () => {
        setOptionsLoading(true);
        try {
            const res = await api.get("/cc/monthly-performance/options/", {params: queryParams});
            setHostOptions((res.data.host_name || []).map((v: string) => ({label: v, value: v})));
            setTierOptions((res.data.club_tier || []).map((v: string) => ({label: v, value: v})));
            setPassportOptions((res.data.passport_country || []).map((v: string) => ({label: v, value: v})));
            setCustNatOptions((res.data.customer_nationality || []).map((v: string) => ({label: v, value: v})));
            setAgentOptions((res.data.agent || []).map((v: string) => ({label: v, value: v})));
        } catch (e: any) {
            message.error(e?.response?.data?.detail || "Failed to load filter options");
        } finally {
            setOptionsLoading(false);
        }
    };

    const loadPermissions = async () => {
        try {
            const res = await api.get("/cc/monthly-performance/permissions/");
            setCanViewWinMetrics(!!res.data?.can_view_win_metrics);
        } catch {
            setCanViewWinMetrics(false);
        }
    };
    useEffect(() => {
        loadPermissions();
    }, []);

    const handleRawTableChange = (
        pagination: TablePaginationConfig,
        _filters: Record<string, any>,
        sorter: SorterResult<RawRow> | SorterResult<RawRow>[]
    ) => {
        const s = Array.isArray(sorter) ? sorter[0] : sorter;

        const ORDERING_MAP: Record<string, string> = {
            agent: "customer__agent",
            customer_name: "customer__last_name",
        };

        const nextPage = pagination.current || 1;
        const nextPageSize = pagination.pageSize || 50;

        let nextOrdering: string | undefined = undefined;

        if (s?.field && s?.order) {
            const field = String(s.field);
            const backendField = ORDERING_MAP[field] || field;
            nextOrdering = s.order === "descend" ? `-${backendField}` : backendField;
        }

        const orderingChanged = nextOrdering !== ordering;

        setPageSize(nextPageSize);
        setOrdering(nextOrdering);

        if (orderingChanged) {
            setPage(1);
        } else {
            setPage(nextPage);
        }
    };

    const refreshAll = async () => {
        setLoading(true);
        try {
            const totalsRes = await api.get("/cc/monthly-performance/aggregate/", {
                params: {...queryParams, group_by: "none"},
            });
            setTotals(totalsRes.data);

            const byMonthRes = await api.get("/cc/monthly-performance/aggregate/", {
                params: {...queryParams, group_by: "month"},
            });
            setByMonth(byMonthRes.data);

            const rawRes = await api.get("/cc/monthly-performance/", {
                params: {...queryParams, page, page_size: pageSize, ordering},
            });
            const results = rawRes.data?.results ?? rawRes.data ?? [];
            setRawRows(results);
            setRawTotal(rawRes.data?.count ?? results.length ?? 0);
        } catch (e: any) {
            message.error(e?.response?.data?.detail || "Failed to load performance data");
        } finally {
            setLoading(false);
        }
    };

    const onUpload = async () => {
        const fileObj = fileList[0]?.originFileObj as File | undefined;
        if (!fileObj) {
            message.error("Choose a CSV file first.");
            return;
        }

        const form = new FormData();
        form.append("month", uploadMonth.startOf("month").format("YYYY-MM-DD"));
        form.append("file", fileObj);

        setUploading(true);
        try {
            const res = await api.post("/cc/monthly-performance/upload/", form, {
                headers: {"Content-Type": "multipart/form-data"},
            });
            message.success(`Imported: ${res.data.row_count} rows (${res.data.month})`);
            setFileList([]);
            await refreshAll();
            await loadOptions();
        } catch (e: any) {
            message.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onResetFilters = () => {
        setHostName(undefined);
        setClubTier(undefined);
        setPassportCountry(undefined);
        setCustomerNationality(undefined);
        setAgent(undefined);
        setPage(1);
    };

    useEffect(() => {
        refreshAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams, page, pageSize, ordering]);

    useEffect(() => {
        loadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams.start, queryParams.end]);

    const baseColumns: TableColumnsType<RawRow> = [
        {title: "Month", dataIndex: "month", key: "month", render: monthLabel, width: 90, fixed: "left"},
        {title: "MMID", dataIndex: "mmid", key: "mmid", width: 110, fixed: "left", ellipsis: true, sorter: true},
        {title: "Customer", dataIndex: "customer_name", key: "customer_name", width: 180, ellipsis: true},
        {title: "Agent", dataIndex: "agent", key: "agent", width: 160, ellipsis: true, sorter: true},
        {title: "Host", dataIndex: "host_name", key: "host_name", width: 160, ellipsis: true},
        {title: "Tier", dataIndex: "club_tier", key: "club_tier", width: 120, ellipsis: true},
        {title: "Country", dataIndex: "passport_country", key: "passport_country", width: 140, ellipsis: true},
        {title: "Days", dataIndex: "days_played", key: "days_played", align: "right", width: 90, sorter: true},

        {
            title: "Total Theo",
            dataIndex: "total_theo",
            key: "total_theo",
            render: fmtInt,
            align: "right",
            width: 130,
            sorter: true
        },

        {
            title: "Slot Theo",
            dataIndex: "slot_theo_win",
            key: "slot_theo_win",
            render: fmtInt,
            align: "right",
            width: 120,
            sorter: true
        },
        {
            title: "Table Theo",
            dataIndex: "tabl_theo_win",
            key: "tabl_theo_win",
            render: fmtInt,
            align: "right",
            width: 120,
            sorter: true
        },
    ];

    const winColumns: TableColumnsType<RawRow> = [
        {
            title: "Total Win",
            dataIndex: "total_win",
            key: "total_win",
            render: fmtInt,
            align: "right",
            width: 130,
            sorter: true
        },
        {
            title: "Slot Win",
            dataIndex: "slot_casino_win",
            key: "slot_casino_win",
            render: fmtInt,
            align: "right",
            width: 120,
            sorter: true
        },
        {
            title: "Table Win",
            dataIndex: "tabl_casino_win",
            key: "tabl_casino_win",
            render: fmtInt,
            align: "right",
            width: 120,
            sorter: true
        },
    ];

// Insert win columns right after Total Theo (index 6 in baseColumns)
    const columns: TableColumnsType<RawRow> = canViewWinMetrics
        ? [...baseColumns.slice(0, 6), ...winColumns, ...baseColumns.slice(6)]
        : baseColumns;

    const monthlyColumns: TableColumnsType<AggMonthRow> = [
        {
            title: "Month",
            dataIndex: "month",
            render: monthLabel,
            width: 90,
        },
        {
            title: "Total Theo",
            dataIndex: "total_theo",
            render: fmtInt,
            align: "right",
        },
        {title: "Days", dataIndex: "days_played", render: fmtInt, align: "right", width: 90},
    ];

    if (canViewWinMetrics) {
        monthlyColumns.push(
            {
                title: "Total Win",
                dataIndex: "total_win",
                render: fmtInt,
                align: "right",
            },
            {
                title: "Slot Win",
                dataIndex: "slot_casino_win",
                render: fmtInt,
                align: "right",
            },
            {
                title: "Table Win",
                dataIndex: "tabl_casino_win",
                render: fmtInt,
                align: "right",
            }
        );
    }

    const kpis: [string, any][] = [
        ["Total Theo", totals?.total_theo],

        ...(canViewWinMetrics
            ? ([
                ["Total Win", totals?.total_win],
                ["Slot Win", totals?.slot_casino_win],
                ["Table Win", totals?.tabl_casino_win],
            ] as [string, any][])
            : []),

        ["Slot Theo", totals?.slot_theo_win],
        ["Table Theo", totals?.tabl_theo_win],
        ["Days Played", totals?.days_played],
    ];


    return (
        <div style={{padding: 4}}>
            {/* Header */}
            <div style={{marginBottom: 12}}>
                <Title level={3} style={{margin: 0}}>
                    Performance
                </Title>
                <Text type="secondary">Monthly performance imports, filters, and reporting.</Text>
            </div>

            {/* Filters */}
            <Card
                bodyStyle={{padding: 12}}
                style={{marginBottom: 12}}
                title="Filters"
                extra={
                    <Space>
                        <Button onClick={onResetFilters}>Reset</Button>
                        <Button type="primary" loading={loading} onClick={refreshAll}>
                            Refresh
                        </Button>
                    </Space>
                }
            >

                <Row gutter={[12, 12]} align="bottom">
                    {/* Date Range */}
                    <Col xs={24} sm={12} md={6}>
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            <Text strong>Date Range</Text>
                            <RangePicker
                                picker="month"
                                value={range}
                                onChange={(v) => {
                                    if (v && v[0] && v[1]) {
                                        setRange([v[0].startOf("month"), v[1].startOf("month")]);
                                        setPage(1);
                                    }
                                }}
                                style={{width: "100%"}}
                            />
                        </div>
                    </Col>

                    {/* Host */}
                    <Col xs={24} sm={12} md={6}>
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            <Text strong>Host</Text>
                            <Select
                                allowClear
                                placeholder="Host"
                                value={hostName}
                                onChange={(v) => {
                                    setHostName(v);
                                    setPage(1);
                                }}
                                options={hostOptions}
                                loading={optionsLoading}
                                showSearch
                            />
                        </div>
                    </Col>

                    {/* Club Tier */}
                    <Col xs={24} sm={12} md={6}>
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            <Text strong>Club Tier</Text>
                            <Select
                                allowClear
                                placeholder="Club Tier"
                                value={clubTier}
                                onChange={(v) => {
                                    setClubTier(v);
                                    setPage(1);
                                }}
                                options={tierOptions}
                                loading={optionsLoading}
                                showSearch
                            />
                        </div>
                    </Col>

                    {/* Passport Country */}
                    <Col xs={24} sm={12} md={6}>
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            <Text strong>Passport Country</Text>
                            <Select
                                allowClear
                                placeholder="Passport Country"
                                value={passportCountry}
                                onChange={(v) => {
                                    setPassportCountry(v);
                                    setPage(1);
                                }}
                                options={passportOptions}
                                loading={optionsLoading}
                                showSearch
                            />
                        </div>
                    </Col>

                    {/* Customer Nationality */}
                    <Col xs={24} sm={12} md={6}>
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            <Text strong>Customer Nationality</Text>
                            <Select
                                allowClear
                                placeholder="Customer Nationality"
                                value={customerNationality}
                                onChange={(v) => {
                                    setCustomerNationality(v);
                                    setPage(1);
                                }}
                                options={custNatOptions}
                                loading={optionsLoading}
                                showSearch
                            />
                        </div>
                    </Col>

                    {/* Agent */}
                    <Col xs={24} sm={12} md={6}>
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            <Text strong>Agent</Text>
                            <Select
                                allowClear
                                placeholder="Agent"
                                value={agent}
                                onChange={(v) => {
                                    setAgent(v);
                                    setPage(1);
                                }}
                                options={agentOptions}
                                loading={optionsLoading}
                                showSearch
                            />
                        </div>
                    </Col>
                </Row>

            </Card>

            {/* KPI row */}
            <Row gutter={[12, 12]} style={{marginBottom: 12}}>
                {kpis.map(([label, value]) => (
                    <Col
                        xs={12}
                        sm={8}
                        md={canViewWinMetrics ? 4 : 6} // nicer spacing when fewer cards
                        key={label}
                    >
                        <Card bodyStyle={{padding: 12}}>
                            <Statistic loading={loading} title={label} value={fmtInt(value)}/>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Monthly Totals */}
            <Card title="Monthly Totals" bodyStyle={{padding: 0}} style={{marginBottom: 12}}>
                <div style={{padding: 12, overflowX: "auto"}}>
                    <Table<AggMonthRow>
                        size="small"
                        loading={loading}
                        rowKey={(r) => r.month}
                        dataSource={byMonth}
                        pagination={false}
                        tableLayout="fixed"
                        style={{width: 390}}
                        scroll={{x: 390}}
                        columns={[
                            {
                                title: "Month",
                                dataIndex: "month",
                                render: monthLabel,
                                width: 110,
                            },
                            {
                                title: "Total Theo",
                                dataIndex: "total_theo",
                                render: fmtInt,
                                align: "right",
                                width: 160,
                            },
                            {
                                title: "Days",
                                dataIndex: "days_played",
                                render: fmtInt,
                                align: "right",
                                width: 120,
                            },
                        ]}
                    />
                </div>
            </Card>

            {/* Data (full width, subtitle near title) */}
            <Card
                bodyStyle={{padding: 0}}
                style={{marginBottom: 12}}
                title={
                    <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                        <span>Data</span>
                        <Text type="secondary" style={{fontSize: 12}}>
                            {rangeSubtitle(range)}
                        </Text>
                    </div>
                }
            >
                <Table<RawRow>
                    loading={loading}
                    rowKey={(r) => `${r.month}-${r.mmid}`}
                    dataSource={rawRows}
                    columns={columns}
                    sticky
                    scroll={{x: 1400, y: 520}}
                    onChange={handleRawTableChange}
                    pagination={{
                        current: page,
                        pageSize,
                        total: rawTotal,
                        showSizeChanger: true,
                    }}
                />
            </Card>

            {/* Upload */}
            <Card title="Monthly CSV Upload" bodyStyle={{padding: 12}}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={8} md={6}>
                        <DatePicker picker="month" value={uploadMonth} onChange={(v) => v && setUploadMonth(v)}
                                    style={{width: "100%"}}/>
                    </Col>
                    <Col xs={24} sm={10} md={12}>
                        <Upload {...uploadProps}>
                            <Button style={{width: "100%"}}>Choose CSV</Button>
                        </Upload>
                    </Col>
                    <Col xs={24} sm={6} md={6}>
                        <Button type="primary" loading={uploading} onClick={onUpload} style={{width: "100%"}}>
                            Upload & Import
                        </Button>
                    </Col>
                </Row>
            </Card>
        </div>
    );
}

export default PerformancePage;
